"use client";

import { ComponentProps, useEffect, useRef, useState } from "react";

import { GrowingTextarea } from "./growingTextarea";

const COMMIT_DELAY_MS = 350;

export const nonBlank = (value?: string) => (value?.trim() ? value : undefined);

type DraftTextareaProps = Omit<
    ComponentProps<typeof GrowingTextarea>,
    "value" | "onValueChange" | "onBlur"
> & {
    value?: string;
    /** Blank saves `undefined`. */
    onCommit: (value: string | undefined) => void;
    /** Saved instead of blank when the author leaves the field (blur or
     *  unmount). While typing, blank isn't saved. */
    fillWhenLeftBlank?: string;
};

/** A GrowingTextarea that saves after a pause or on blur, like RichText. */
export function DraftTextarea({
    value,
    onCommit,
    fillWhenLeftBlank,
    ...rest
}: DraftTextareaProps) {
    const stored = nonBlank(value);
    const [draft, setDraft] = useState(stored ?? "");
    const [synced, setSynced] = useState(stored);
    // An outside change (undo) replaces the draft; our own commits match it.
    if (stored !== synced) {
        setSynced(stored);
        if (nonBlank(draft) !== stored) setDraft(stored ?? "");
    }

    // Debounced like RichText, so undo steps back by pause, not by key.
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const commitRef = useRef((_leaving: boolean) => {});
    commitRef.current = (leaving) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        let next = nonBlank(draft);
        if (!next && fillWhenLeftBlank !== undefined) {
            // Data that was already blank is left alone: no writes on open.
            if (!leaving || !stored) return;
            next = fillWhenLeftBlank;
            setDraft(next);
        }
        if (next !== stored) onCommit(next);
    };
    useEffect(() => () => commitRef.current(true), []);

    return (
        <GrowingTextarea
            {...rest}
            value={draft}
            onValueChange={(next) => {
                setDraft(next);
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(
                    () => commitRef.current(false),
                    COMMIT_DELAY_MS
                );
            }}
            onBlur={() => commitRef.current(true)}
        />
    );
}
